import { BlogCard } from './types';

export const gpBlogCards: BlogCard[] = [
  {
    title: {
      content:
        "### Can't Get a GP Appointment? Here Is How to Get a Private Sick Note in London Today",
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },
      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black',
      },
    },
    description: {
      content:
        'Private sick note in London: get a valid fit note from a GP the same day, no wait',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'get-your-sicknote.webp',
    bannerUrl: 'sick-note-banner.webp',
    slug: 'how-to-get-a-private-sick-note-in-london',
    contentType: 'article',
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        { text: 'What is a private sick note?', id: 'what-is-sick-note' },
        { text: 'Who can write a sick note?', id: 'who-can-write-sick-note' },
        {
          text: 'What happens at the appointment',
          id: 'what-happens-appointment',
        },
        { text: 'Private sick note vs NHS sick note', id: 'private-vs-nhs' },
        {
          text: 'How much does a private sick note cost?',
          id: 'sick-note-cost',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    topics: {
      title: {
        content: '#### TOPICS:',
        styles: {
          h4: 'text-theme-primary font-extrabold p-2',
        },
      },
      topics: [
        'Private Sick Note',
        'Fit Note London',
        'Private GP London',
        'Sick Note Today',
        'Medical Certificate',
      ],
      styles: {
        p: 'p-2 bg-info-500-opac-10 text-teal-450 bg-info-500-soft',
      },
    },
    speciality: 'GP - General Practice',
    date: {
      content: '28, May 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    author: {
      content: 'LIPS Healthcare Marketing Team',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    content: {
      content:
        "### Private sick note in London: get a valid fit note from a GP the same day, no wait\n\n A private sick note is just as legally valid as an NHS one, and your employer cannot refuse it. Most people do not know that. They assume a fit note only counts if it comes from an NHS GP, so they wait days for an appointment they do not need to wait for. If you are unwell and need documentation for work, a registered [private GP](/private-gp) can assess you and issue a fit note today. By the end of this article you will know exactly how the process works, what your employer can and cannot ask for, and what to expect at the appointment so there are no surprises.\n\n<span id='what-is-sick-note'></span>\n\n### What is a private sick note and will your employer accept it?\n\nYour employer must accept a fit note signed by any registered GP, whether that GP works privately or on the NHS. A sick note from a private doctor is not a second-tier document. It is the same form, carries the same legal weight, and is recognised by the same organisations: your employer, HMRC, and the Department for Work and Pensions. The official name for a sick note is a Statement of Fitness for Work. It tells your employer either that you are not well enough to work, or that you can return to work only if certain changes are made, such as reduced hours or lighter duties. Your GP decides which option applies based on your assessment.\n\nIf your employer ever questions whether a private fit note is valid, you can point them to the [GOV.UK fit note guidance](https://www.gov.uk/government/collections/fit-note), which confirms there is no legal difference between a private and an NHS issued note.\n\n<span id='who-can-write-sick-note'></span>\n\n### Who is allowed to write a sick note?\n\nOnly a doctor or other registered healthcare professional who has actually assessed you can write a fit note. That assessment must happen in a proper consultation, face to face or by video. A sick note cannot be issued over the phone without a clinical assessment taking place. At LIPS Healthcare, every appointment is with a fully qualified, GMC registered GP. Your fit note will show the doctor's name, GMC number, the clinic name, and the Battersea Power Station address. Those are the details your employer or HR team may want to check, and they will all be on the document you receive.\n\n<strong><a href='/gp-access#booking-calendar-widget' target='_blank' rel='noopener noreferrer'>Book a same-day GP appointment</a></strong>\n\n<span id='what-happens-appointment'></span>\n\n### What happens at the appointment: a step by step guide\n\nA private sick note appointment at [LIPS](/private-gp) is a focused consultation, not a long general check up. Here is exactly what to expect so there are no surprises.\n\nWhen you arrive, the GP will ask you to describe how you are feeling in your own words. They will ask how long you have been unwell, how it is affecting your daily life, and how it is stopping you from doing your job. They may ask a few follow-up questions and do a brief check if needed. You do not need to bring any letters, referrals, or old medical records. If you have them, bring them along as they can be useful, but they are not required.\n\nThe GP will then decide, based on what they hear and see, whether your situation supports issuing a fit note. If it does, they complete it during the same appointment. The note will state the period it covers and whether you are signed off completely or able to return with adjustments. You leave the clinic with it the same day.\n\nYour notes from that appointment are saved in your LIPS patient record. If you need your sick note extended or have a follow up, the GP will already have the background from your first visit, so you will not need to start from scratch.\n\nLIPS Healthcare is at Battersea Power Station, London SW8. Easy to reach from Chelsea, Clapham, Vauxhall, Pimlico, and Wandsworth via the Northern line, Overground, or bus.\n\n<span id='private-vs-nhs'></span>\n\n### Private sick note vs NHS sick note: is there actually a difference?\n\nThe document is the same. The legal status is the same. The only difference is how quickly you can get one. An NHS sick note is free if you are registered with a GP and can get an appointment. The problem is that NHS appointments in London can take several days or longer, and if your employer needs paperwork now, waiting is not always an option. A private sick note means you can book for today, see a qualified GP, and have your fit note in hand before the working day is over. There is a fee for the consultation, but there is no waiting list and no referral needed.\n\n<strong><a href='/gp-access#booking-calendar-widget' target='_blank' rel='noopener noreferrer'>Book a same-day GP appointment</a></strong>\n\n<span id='sick-note-cost'></span>\n\n### How much does a private sick note cost in London?\n\nA private sick note at LIPS Healthcare is included as part of your GP consultation fee. There is no separate charge for the document itself. Visit [gp-access](/private-gp) or call the clinic for current pricing, as fees are confirmed when you book.\n\nThe consultation fee covers the GP's time to assess you properly and produce a document that is medically sound and legally accepted. If you need anything else at the same visit, such as a prescription or a referral, the GP will let you know the cost before anything is added. Nothing is charged without your agreement.",
      styles: {
        h3: 'text-lg md:text-xl font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'Can a private GP legally write a sick note?',
          },
          answer: {
            content:
              'Yes. Any GMC-registered private GP can write a legally valid fit note in the UK after assessing you. It is accepted by employers, HMRC, and the DWP.',
          },
        },
        {
          question: {
            content: 'Does my employer have to accept a private sick note?',
          },
          answer: {
            content:
              'Yes. The law does not distinguish between private and NHS fit notes. If a registered doctor signed it after assessing you, your employer cannot refuse it.',
          },
        },
        {
          question: {
            content: 'How fast can I get a private sick note in London?',
          },
          answer: {
            content:
              'Same day. At LIPS Healthcare, Battersea Power Station, same-day appointments are available. You book, see the GP, and leave with your fit note in one visit.',
          },
        },
        {
          question: {
            content: 'Do I need to be an existing patient at LIPS?',
          },
          answer: {
            content:
              'No. You can book as a new patient with no prior registration. The GP will assess you at your first appointment and issue the fit note if appropriate.',
          },
        },
        {
          question: {
            content:
              'Can I get a private sick note for stress or a mental health condition?',
          },
          answer: {
            content:
              'Yes. GPs at LIPS assess all conditions, including stress, anxiety, and depression. If your condition is affecting your ability to work, the GP can issue a fit note that reflects that.',
          },
        },
      ],
    },
    postFaqContent: {
      content:
        "<strong><a href='/gp-access#booking-calendar-widget' target='_blank' rel='noopener noreferrer'>Book a same-day GP appointment</a></strong>\n\n> ⓘ Medical disclaimer: This article is for informational purposes only and does not constitute medical advice. A fit note can only be issued following a proper clinical assessment by a registered healthcare professional. If you are unwell and need medical attention, consult a qualified GP.\n\nSources :\n\n[NHS fit note guidance](https://www.nhs.uk/conditions/fit-note/)\n\n[GOV.UK](https://www.gov.uk/government/collections/fit-note)",
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'inline-block text-theme-primary font-bold no-underline text-center',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-secondary border border-blue-200 rounded-lg p-4 my-8 text-sm text-text-default not-italic',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer mt-8 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
      },
    },
  },
  {
    title: {
      content:
        '### Tired of Waiting Weeks for a GP? Here is How to Book a GP Appointment Online',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },
      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black',
      },
    },
    description: {
      content:
        'Booking a GP appointment online means choosing a time, confirming your slot, and seeing a doctor on your terms, without the wait.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'book-a-gp.webp',
    bannerUrl: 'book-a-gp-banner.webp',
    slug: 'gp-appointment-online-london',
    contentType: 'article',
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What does booking a GP appointment online mean?',
          id: 'what-does-booking-mean',
        },
        { text: 'How to book a GP appointment online', id: 'how-to-book-gp' },
        {
          text: 'What does a private GP appointment include?',
          id: 'what-does-gp-include',
        },
        {
          text: 'Booking online vs NHS: when does private make sense?',
          id: 'gp-vs-nhs',
        },
        { text: 'How much does a private GP appointment cost?', id: 'gp-cost' },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    topics: {
      title: {
        content: '#### TOPICS:',
        styles: {
          h4: 'text-theme-primary font-extrabold p-2',
        },
      },
      topics: [
        'General Practice',
        'Private GP',
        'Online Booking',
        'Same Day Appointment',
      ],
      styles: {
        p: 'p-2 bg-info-500-opac-10 text-teal-450 bg-info-500-soft',
      },
    },
    speciality: 'GP - General Practice',
    date: {
      content: '19, May 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    author: {
      content: 'LIPS Healthcare Marketing Team',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    content: {
      content:
        "### GP appointment online in London: see a doctor today without the NHS queue\n\nBooking a GP appointment online means choosing a time, confirming your slot, and seeing a doctor on your terms, without submitting a request and waiting to hear back. Private clinics in London, including LIPS Healthcare at Battersea Power Station, offer same-day and next-day appointments bookable directly online, with no referral and no prior registration needed. This guide explains how online GP booking works, what to expect from a private appointment, how it compares to the NHS route, and what it costs.\n\n<span id='what-does-booking-mean'></span>\n\n### What does booking a GP appointment online actually mean?\n\nIt depends entirely on where you are booking, and the difference is bigger than most people expect.\n\nThrough the NHS, booking online usually means submitting a request through the NHS App or your practice's website. You are placed into a triage queue and the practice decides how and when to respond. Whether you get a face-to-face slot, a phone call, or a message back depends on how they categorise your concern. When you actually get seen is largely out of your hands.\n\nThrough a private clinic, it works the way you would hope. You see the available times, pick one, and confirm. Your appointment is locked in straight away. No triage, no uncertainty, no waiting to find out whether you made the cut.\n\nAt LIPS Healthcare, same-day and next-day GP appointments are bookable directly at [LIPS Healthcare](/gp-access). No NHS registration needed, no referral required.\n\n<span id='how-to-book-gp'></span>\n\n### How to book a GP appointment online in London\n\nMost private GP clinics in London offer direct online booking. The general process is consistent across providers: visit the clinic's website, choose your appointment type, select an available time from a live calendar, fill in a short form with your name and reason for visiting, and receive instant confirmation. You do not need to be registered with the clinic beforehand, and most private clinics do not require a GP referral.\n\nWhat varies between providers is availability, what the appointment includes, and what happens next. Some online GP services are video-only consultations. Others, like LIPS Healthcare, are in-person appointments at a physical clinic, which means your GP can examine you, arrange on-site tests where needed, and issue a prescription the same day.\n\nWhen choosing where to book, it is worth confirming: is the appointment in person or by video? Are blood tests available on site? Can you get a prescription the same day? The answers will shape whether a particular provider suits your situation.\n\nAt LIPS Healthcare, you book at [GP on LIPS Healthcare](/gp-access), choose a slot from live availability, and confirm in a couple of steps. Same-day slots are updated throughout the day.\n\n<strong><a href='/gp-access#booking-calendar-widget' target='_blank' rel='noopener noreferrer'>Book a GP appointment</a></strong>\n\n<span id='what-does-gp-include'></span>\n\n### What does a private GP appointment booked online include?\n\nIt is worth understanding what a private GP appointment actually covers, because it varies more than people expect.\n\nA standard private GP consultation typically runs 20 to 30 minutes, which is longer than the average NHS slot. Your GP takes a full history, examines you where relevant, and explains their assessment in plain language. You leave with a clear picture of what was found and what the next step is.\n\nAt LIPS Healthcare, a single appointment can also include:\n\n- *[On-site Blood Test](/gp-access)* With results often available the same day, so your GP can review them and act before you leave.\n\n- *A private prescription* Dispensed through the LIPS pharmacy immediately after your consultation, so you leave with your medication in hand.\n\n- *A sick note* Issued on the day, legally valid and accepted by employers in the same way as an NHS fit note.\n\n- *A same-day specialist referral* To any of LIPS's 44 clinical specialities, with no waiting list.\n\nFor many patients, what would typically require two or three NHS appointments spread over several weeks can be handled in a single visit.\n\n<span id='gp-vs-nhs'></span>\n\n### Booking a GP appointment online vs the NHS: when does private make sense?\n\nFor non-urgent concerns where you are happy to wait, the NHS works well. If you need a routine follow-up, a repeat prescription review, or have a minor issue with no time pressure, there is no pressing reason to go private.\n\nThe calculation changes when time matters. NHS England data shows that in 2024, around one in five GP appointments in England involved a wait of more than two weeks (NHS England, 2024). In London, same-day NHS slots are often fully taken before 8:30am. Even submitting a request first thing does not guarantee you will be seen that day.\n\nWhen you book online GP appointments through a private clinic, your slot is confirmed the moment you choose it. That certainty is the practical difference between the two routes. Same-day availability at LIPS Healthcare is shown live at [LIPS](/gp-access) and updated throughout the day.\n\nThe NHS is free. Private GP care has a fee. If you are not time-pressured, the NHS route is entirely reasonable. If you need to see a doctor today and cannot get through, booking online with a private clinic is a reliable alternative.\n\nLIPS Healthcare is at Battersea Power Station, London SW8, accessible via the Northern line from Chelsea, Clapham, Vauxhall, Pimlico, and Wandsworth.\n\n> If you have chest pain, difficulty breathing, signs of a stroke, severe bleeding, or loss of consciousness, call 999 or go to your nearest NHS A&E immediately. Not sure if it is an emergency? Call NHS 111 or visit [111.nhs.uk](/gp-access).\n\n<span id='gp-cost'></span>\n\n### How much does a private GP appointment cost when you book online?\n\nPrivate GP fees vary between clinics and between appointment types, so it is worth checking directly before you book.\n\nA few things that typically affect the total cost:\n\n- Whether it is a new patient consultation or a follow-up\n- How long the appointment runs\n- Whether blood tests or other on-site diagnostics are needed\n- Whether a prescription is issued\n\nBlood tests, prescriptions, and specialist referrals are generally charged separately from the consultation fee. Your GP will explain what is clinically appropriate and confirm any additional costs before anything is arranged, so there are no surprises.\n\nIf you have private health insurance, check with your insurer before booking. Many policies cover GP consultations. Some clinics require upfront payment with a claim submitted afterwards; others can bill your insurer directly. It is worth confirming this at the point of booking.\n\nCurrent pricing at LIPS Healthcare is confirmed when you book. Visit [LIPS Booking](/gp-access) for available slots and fee information.",
      styles: {
        h3: 'text-lg md:text-xl font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'text-theme-primary [&_p]:inline not-italic font-bold [&_a]:font-bold',
        ul: 'list-disc pl-6 space-y-2 mb-2',
        li: 'text-text-default leading-relaxed',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        blockquote:
          'border-l-4 border-theme-primary pl-2 text-sm not-italic [&_p]:text-royal-900 [&_p]:font-medium',
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content:
              'Can I book a GP appointment online without an NHS referral?',
          },
          answer: {
            content:
              'Yes. LIPS Healthcare accepts direct bookings at [gp-access](/gp-access) with no referral and no prior registration. You pick a time, fill in a short form, and your appointment is confirmed.',
          },
        },
        {
          question: {
            content:
              'Can I get a same-day GP appointment online at LIPS Healthcare?',
          },
          answer: {
            content:
              'Yes, subject to availability. Same-day slots are shown in real time at [gp-access](/gp-access) and updated throughout the day. If nothing same-day is showing, next-day slots are listed.',
          },
        },
        {
          question: {
            content:
              'What is the difference between an online GP consultation and booking a GP appointment online?',
          },
          answer: {
            content:
              'An online GP consultation is a video or phone appointment. Booking a GP appointment online means arranging a confirmed, in-person visit at a clinic. LIPS Healthcare offers face-to-face appointments you book digitally and attend at Battersea Power Station.',
          },
        },
        {
          question: {
            content:
              'Can I get a sick note from a private GP appointment booked online?',
          },
          answer: {
            content:
              'Yes. If it is clinically appropriate, your LIPS GP issues a fit note on the same day. Private fit notes are legally valid and accepted by employers in the same way as NHS ones.',
          },
        },
        {
          question: {
            content:
              'Do I need to register with LIPS Healthcare before I book?',
          },
          answer: {
            content:
              'No. You fill in a short form at the point of booking. That is it. Your appointment is confirmed straight away.',
          },
        },
      ],
    },
    postFaqContent: {
      content:
        "<strong><a href='/gp-access#booking-calendar-widget' target='_blank' rel='noopener noreferrer'>Book a GP appointment</a></strong>\n\n> ⓘ Medical disclaimer: This article is for informational purposes only and does not constitute medical advice. If you are experiencing a medical emergency, call 999 immediately. For urgent non-emergency advice, call NHS 111.\n\nSources : [NHS England GP appointment data 2024](/gp-access)",
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'inline-block text-theme-primary font-bold no-underline text-center',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-secondary border border-blue-200 rounded-lg p-4 my-8 text-sm text-text-default not-italic',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer mt-8 text-center  [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
      },
    },
  },
];
